import { z } from 'zod';
export const platformModuleIdSchema = z
    .string()
    .min(2)
    .max(64)
    .regex(/^[a-z][a-z0-9]*(?:-[a-z0-9]+)*$/);
export const platformDatabaseSchemaNameSchema = z
    .string()
    .min(2)
    .max(64)
    .regex(/^[a-z][a-z0-9]*(?:_[a-z0-9]+)*$/);
export function platformDatabaseSchemaForModule(moduleId) {
    return platformModuleIdSchema.parse(moduleId).replaceAll('-', '_');
}
export const platformVersionedIdentifierSchema = z
    .string()
    .min(5)
    .max(160)
    .regex(/^[a-z][a-z0-9-]*(?:\.[a-z0-9-]+)+\.v[1-9][0-9]*$/);
const platformQualifiedIdentifierSchema = z
    .string()
    .min(3)
    .max(160)
    .regex(/^[a-z][a-z0-9_-]*(?:\.[a-z0-9_-]+)+$/);
function uniqueArray(item, label) {
    return z.array(item).superRefine((values, context) => {
        const seen = new Set();
        for (const [index, value] of values.entries()) {
            if (seen.has(value)) {
                context.addIssue({
                    code: 'custom',
                    message: `Duplicate ${label}: ${String(value)}`,
                    path: [index],
                });
            }
            seen.add(value);
        }
    });
}
const platformContributionSchema = z.strictObject({
    id: platformQualifiedIdentifierSchema,
    requiredCapability: platformVersionedIdentifierSchema.optional(),
    requiredPermission: platformQualifiedIdentifierSchema.optional(),
    priority: z.number().int().min(-1000).max(1000).default(0),
});
const platformModuleFailurePolicySchema = z.strictObject({
    consecutiveFailureThreshold: z.number().int().min(1).max(100),
    openDurationMs: z.number().int().min(100).max(3_600_000),
    halfOpenConcurrency: z.number().int().min(1).max(100),
}).default({
    consecutiveFailureThreshold: 5,
    openDurationMs: 30_000,
    halfOpenConcurrency: 1,
});
export const platformModuleLifecyclePolicySchema = z.strictObject({
    initialization: z.strictObject({
        timeoutMs: z.number().int().min(100).max(120_000),
        failureBehavior: z.enum(['reject-release', 'quarantine-module']),
    }),
    resources: z.strictObject({
        requestConcurrency: z.number().int().min(1).max(10_000),
        jobConcurrency: z.number().int().min(0).max(10_000),
        queueBacklogLimit: z.number().int().min(0).max(10_000_000),
        databaseConnectionBudget: z.number().int().min(1).max(1_000),
        workerIsolation: z.enum(['shared-api', 'separate-worker']),
        failures: platformModuleFailurePolicySchema,
    }),
    disable: z.strictObject({
        data: z.literal('retain'),
        clientContributions: z.literal('remove'),
        serverRoutes: z.literal('deny-tenant-access'),
        scheduledJobs: z.enum(['skip', 'bounded-catch-up', 'recompute-current-state']),
        events: z.strictObject({
            behavior: z.enum([
                'pause-and-replay',
                'skip-and-reconcile',
                'rebuild-projection',
                'drop-safe',
            ]),
            durableCursor: z.boolean(),
            retentionDays: z.number().int().min(0).max(3_650),
            backlogLimit: z.number().int().min(0).max(10_000_000),
            warningAt: z.number().int().min(0).max(10_000_000),
            reconciliationProcedure: platformVersionedIdentifierSchema.nullable(),
            dropSafeReason: z.string().trim().min(10).max(500).nullable(),
        }),
        outboundSideEffects: z.literal('suppress-no-retroactive-send'),
        inFlight: z.enum(['finish-idempotently', 'fence-and-retry']),
    }),
    resume: z.strictObject({
        configuration: z.literal('last-compatible-published'),
        healthChecks: uniqueArray(platformQualifiedIdentifierSchema, 'resume health check').min(1),
        reconciliationProcedure: platformVersionedIdentifierSchema.nullable(),
        visibility: z.literal('after-health-and-reconciliation'),
    }),
    uninstall: z.strictObject({
        executableCode: z.literal('remove'),
        data: z.literal('retain'),
        tombstone: z.literal('retain-immutable'),
        destruction: z.literal('separate-explicit-authority'),
    }),
});
export const platformModuleManifestSchema = z.strictObject({
    id: platformModuleIdSchema,
    version: z.string().regex(/^[0-9]+\.[0-9]+\.[0-9]+$/),
    kind: z.enum(['platform', 'business']),
    required: z.boolean(),
    requires: uniqueArray(platformModuleIdSchema, 'module dependency'),
    requiredCapabilities: uniqueArray(platformVersionedIdentifierSchema, 'required capability'),
    optionalCapabilities: uniqueArray(platformVersionedIdentifierSchema, 'optional capability'),
    optionalCapabilityFallbacks: uniqueArray(z.strictObject({
        capability: platformVersionedIdentifierSchema,
        degradedMode: platformVersionedIdentifierSchema,
    }), 'optional capability fallback'),
    provides: uniqueArray(platformVersionedIdentifierSchema, 'provided capability'),
    permissions: uniqueArray(platformQualifiedIdentifierSchema, 'permission'),
    settings: uniqueArray(platformQualifiedIdentifierSchema, 'setting'),
    copyCatalogs: uniqueArray(platformQualifiedIdentifierSchema, 'copy catalog'),
    events: z.strictObject({
        publishes: uniqueArray(platformVersionedIdentifierSchema, 'published event'),
        subscribes: uniqueArray(platformVersionedIdentifierSchema, 'subscribed event'),
    }),
    server: z.strictObject({
        routes: z.array(platformContributionSchema),
        jobs: z.array(platformContributionSchema),
        subscribers: z.array(platformContributionSchema),
        slots: z.array(platformContributionSchema),
    }),
    web: z.strictObject({
        routes: z.array(platformContributionSchema),
        navigation: z.array(platformContributionSchema),
        slots: z.array(platformContributionSchema),
    }),
    mobile: z.strictObject({
        routes: z.array(platformContributionSchema),
        slots: z.array(platformContributionSchema),
    }),
    portal: z.strictObject({
        routes: z.array(platformContributionSchema),
        slots: z.array(platformContributionSchema),
    }),
    database: z.strictObject({
        schema: platformDatabaseSchemaNameSchema,
        owns: uniqueArray(platformQualifiedIdentifierSchema, 'owned database object'),
        readsThrough: uniqueArray(platformVersionedIdentifierSchema, 'database read contract'),
        migrations: uniqueArray(platformQualifiedIdentifierSchema, 'migration'),
    }),
    health: uniqueArray(platformQualifiedIdentifierSchema, 'health check').min(1),
    lifecycle: platformModuleLifecyclePolicySchema,
    retentionPolicy: z.string().min(1).max(160),
}).superRefine((manifest, context) => {
    const requiredCapabilities = new Set(manifest.requiredCapabilities);
    const optionalCapabilities = new Set(manifest.optionalCapabilities);
    const providedCapabilities = new Set(manifest.provides);
    const declaredCapabilities = new Set([
        ...requiredCapabilities,
        ...optionalCapabilities,
        ...providedCapabilities,
    ]);
    const permissions = new Set(manifest.permissions);
    const healthChecks = new Set(manifest.health);
    if (manifest.requires.includes(manifest.id)) {
        context.addIssue({ code: 'custom', message: 'A module cannot require itself.', path: ['requires'] });
    }
    for (const capability of requiredCapabilities) {
        if (optionalCapabilities.has(capability)) {
            context.addIssue({
                code: 'custom',
                message: `Capability cannot be both required and optional: ${capability}`,
                path: ['optionalCapabilities'],
            });
        }
        if (providedCapabilities.has(capability)) {
            context.addIssue({
                code: 'custom',
                message: `A module cannot require a capability that it provides itself: ${capability}`,
                path: ['requiredCapabilities'],
            });
        }
    }
    for (const capability of optionalCapabilities) {
        if (providedCapabilities.has(capability)) {
            context.addIssue({
                code: 'custom',
                message: `A module cannot optionally consume a capability that it provides itself: ${capability}`,
                path: ['optionalCapabilities'],
            });
        }
    }
    const fallbackCapabilities = manifest.optionalCapabilityFallbacks.map(({ capability }) => capability);
    const uniqueFallbackCapabilities = new Set(fallbackCapabilities);
    if (fallbackCapabilities.length !== manifest.optionalCapabilities.length ||
        uniqueFallbackCapabilities.size !== fallbackCapabilities.length ||
        fallbackCapabilities.some((capability) => !optionalCapabilities.has(capability))) {
        context.addIssue({
            code: 'custom',
            message: 'Every optional capability must have exactly one declared degraded-mode fallback.',
            path: ['optionalCapabilityFallbacks'],
        });
    }
    const contributions = [
        ...manifest.server.routes,
        ...manifest.server.jobs,
        ...manifest.server.subscribers,
        ...manifest.server.slots,
        ...manifest.web.routes,
        ...manifest.web.navigation,
        ...manifest.web.slots,
        ...manifest.mobile.routes,
        ...manifest.mobile.slots,
        ...manifest.portal.routes,
        ...manifest.portal.slots,
    ];
    for (const contribution of contributions) {
        if (contribution.requiredCapability !== undefined &&
            !declaredCapabilities.has(contribution.requiredCapability)) {
            context.addIssue({
                code: 'custom',
                message: `Contribution ${contribution.id} uses undeclared capability ${contribution.requiredCapability}.`,
                path: ['requiredCapabilities'],
            });
        }
        if (contribution.requiredPermission !== undefined &&
            !permissions.has(contribution.requiredPermission)) {
            context.addIssue({
                code: 'custom',
                message: `Contribution ${contribution.id} uses undeclared permission ${contribution.requiredPermission}.`,
                path: ['permissions'],
            });
        }
    }
    if (manifest.server.jobs.length > 0 && manifest.lifecycle.resources.jobConcurrency < 1) {
        context.addIssue({
            code: 'custom',
            message: 'A module with registered jobs must declare a positive job-concurrency budget.',
            path: ['lifecycle', 'resources', 'jobConcurrency'],
        });
    }
    for (const capability of manifest.database.readsThrough) {
        if (!requiredCapabilities.has(capability) && !optionalCapabilities.has(capability)) {
            context.addIssue({
                code: 'custom',
                message: `Database read contract must be a required or optional capability: ${capability}`,
                path: ['database', 'readsThrough'],
            });
        }
    }
    const expectedFailureBehavior = manifest.required ? 'reject-release' : 'quarantine-module';
    if (manifest.lifecycle.initialization.failureBehavior !== expectedFailureBehavior) {
        context.addIssue({
            code: 'custom',
            message: `${manifest.required ? 'Mandatory' : 'Optional'} module initialization must use ${expectedFailureBehavior}.`,
            path: ['lifecycle', 'initialization', 'failureBehavior'],
        });
    }
    for (const healthCheck of manifest.lifecycle.resume.healthChecks) {
        if (!healthChecks.has(healthCheck)) {
            context.addIssue({
                code: 'custom',
                message: `Resume health check is not declared by the module: ${healthCheck}`,
                path: ['lifecycle', 'resume', 'healthChecks'],
            });
        }
    }
    const eventPolicy = manifest.lifecycle.disable.events;
    const expectedDatabaseSchema = platformDatabaseSchemaForModule(manifest.id);
    if (manifest.database.schema !== expectedDatabaseSchema) {
        context.addIssue({
            code: 'custom',
            message: `Module ${manifest.id} must own database schema ${expectedDatabaseSchema}.`,
            path: ['database', 'schema'],
        });
    }
    if (eventPolicy.behavior === 'drop-safe') {
        if (eventPolicy.durableCursor ||
            eventPolicy.retentionDays !== 0 ||
            eventPolicy.backlogLimit !== 0 ||
            eventPolicy.warningAt !== 0 ||
            eventPolicy.reconciliationProcedure !== null ||
            eventPolicy.dropSafeReason === null) {
            context.addIssue({
                code: 'custom',
                message: 'drop-safe requires no cursor/backlog and an explicit no-replay reason.',
                path: ['lifecycle', 'disable', 'events'],
            });
        }
    }
    else if (!eventPolicy.durableCursor ||
        eventPolicy.retentionDays < 1 ||
        eventPolicy.backlogLimit < 1 ||
        eventPolicy.warningAt < 1 ||
        eventPolicy.warningAt > eventPolicy.backlogLimit ||
        eventPolicy.reconciliationProcedure === null ||
        eventPolicy.dropSafeReason !== null ||
        manifest.lifecycle.resume.reconciliationProcedure === null) {
        context.addIssue({
            code: 'custom',
            message: 'Replay/reconcile event policies require a durable cursor, bounded retention/backlog, warning threshold, and reconciliation procedures.',
            path: ['lifecycle', 'disable', 'events'],
        });
    }
});
export function defineCrestModule(manifest) {
    return platformModuleManifestSchema.parse(manifest);
}
//# sourceMappingURL=platform-module-manifest.contract.js.map